import { useState } from "react";

const SCALE = 0.7;
const WIDTH = 860;
const HEIGHT = 520;

const MINI_STYLE = `
  .pvm-btn { color: white; background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; padding: 0 10px; font-family: Tahoma, sans-serif; font-size: 14pt; transition: color 0.12s; height: 100%; white-space: nowrap; }
  .pvm-btn:hover, .pvm-btn:hover * { color: #93c5fd !important; }
  .pvm-btn-under { color: white; background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; padding: 0 8px; font-family: Tahoma, sans-serif; font-size: 12pt; transition: color 0.12s; height: 100%; }
  .pvm-btn-under:hover, .pvm-btn-under:hover * { color: #93c5fd !important; }
  .pvm-nav-left { border-right: 2px solid #fff; height: 100%; display: inline-flex; align-items: center; padding-left: 5px; }
  .pvm-nav-right { border-left: 2px solid #fff; height: 100%; display: inline-flex; align-items: center; padding-left: 5px; }
  .pvm-ic { background-image: url('/default.png'); background-repeat: no-repeat; }
  .pvm-ic-end { background-position: 0 -1188px; padding-left: 28px !important; }
  .pvm-ic-end:hover { background-position: 0 -1231px; }
  .pvm-ic-flag { background-position: 0 -970px; padding-left: 26px !important; }
  .pvm-ic-flag:hover { background-position: 0 -1015px; }
  .pvm-ic-flag-sel { background-position: 0 -1060px; padding-left: 26px !important; }
  .pvm-ic-prev { background-position: 0 -1618px; padding-left: 22px !important; }
  .pvm-ic-prev:hover { background-position: 0 -1660px; }
  .pvm-ic-nav { background-position: 0 -1702px; padding-left: 22px !important; }
  .pvm-ic-nav:hover { background-position: 0 -1745px; }
  .pvm-ic-next { background-position: right -1828px; padding-right: 22px !important; }
  .pvm-ic-next:hover { background-position: right -1869px; }
`;

const QUESTIONS = [
  {
    num: "4 of 20",
    time: "71:38",
    html: "The function f is defined by f(<em>x</em>)&nbsp;=&nbsp;<em>x</em><sup>3</sup>&nbsp;&minus;&nbsp;3<em>x</em>. It has a local maximum at <em>x</em>&nbsp;=&nbsp;<em>a</em> and a local minimum at <em>x</em>&nbsp;=&nbsp;<em>b</em>. Find the value of f(<em>a</em>)&nbsp;&minus;&nbsp;f(<em>b</em>).",
    options: ["0", "2", "4", "6", "8"],
  },
  {
    num: "11 of 20",
    time: "57:04",
    html: "Find the area of the region enclosed between the curve <em>y</em>&nbsp;=&nbsp;<em>x</em>(4&nbsp;&minus;&nbsp;<em>x</em>) and the <em>x</em>-axis.",
    options: ["8", "16⁄3", "32⁄3", "16", "64⁄3"],
  },
  {
    num: "16 of 20",
    time: "44:51",
    html: "A geometric sequence has first term <em>a</em> and common ratio <em>r</em>. The sum of the first 3 terms is 7 and the sum of the first 6 terms is 63. Find the value of <em>r</em>.",
    options: ["1", "2", "3", "4", "9"],
  },
];

function PetalSpinner() {
  return (
    <svg width="40" height="40" viewBox="0 0 64 64">
      {Array.from({ length: 12 }).map((_, index) => (
        <rect key={index} x="29" y="4" width="6" height="16" rx="3" fill="rgb(50,80,160)" transform={`rotate(${index * 30} 32 32)`} opacity="0.15">
          <animate attributeName="opacity" values="1;0.15" dur="1s" begin={`${-(1 - index / 12)}s`} repeatCount="indefinite" />
        </rect>
      ))}
    </svg>
  );
}

export default function SimulatorPreview() {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [visited, setVisited] = useState(new Set([0]));
  const [transitioning, setTransitioning] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [showNavigator, setShowNavigator] = useState(false);

  const selected = answers[questionIndex] ?? null;
  const question = QUESTIONS[questionIndex];
  const excess = HEIGHT * (1 - SCALE);
  const clippedWidth = Math.round(WIDTH * SCALE);

  const getStatus = index => {
    if (answers[index] !== undefined) return "Complete";
    if (visited.has(index)) return "Incomplete";
    return "Unseen";
  };

  const goTo = index => {
    if (transitioning) return;
    setTransitioning(true);
    window.setTimeout(() => {
      setQuestionIndex(index);
      setVisited(current => new Set([...current, index]));
      setTransitioning(false);
    }, 360);
  };

  const navigate = direction => {
    if (transitioning) return;
    if (direction === 1) setShowHint(false);
    goTo((questionIndex + direction + QUESTIONS.length) % QUESTIONS.length);
  };

  const scaledNavHeight = Math.round(33 * SCALE);

  return (
    <div className="relative px-5 pb-5 sm:px-7">
      <div
        className="pointer-events-none absolute z-10 transition-opacity duration-500"
        style={{ bottom: 20 + scaledNavHeight + 10, right: 34, opacity: showHint ? 1 : 0, animation: showHint ? "float 2.2s ease-in-out infinite" : "none" }}
      >
        <div className="relative whitespace-nowrap rounded-lg border border-white/10 bg-zinc-950/90 px-3 py-1.5 text-[11px] text-zinc-300 shadow-xl backdrop-blur-md">
          Click Next to try it
          <span className="absolute -bottom-1 right-4 h-2 w-2 rotate-45 border-b border-r border-white/10 bg-zinc-950" />
        </div>
      </div>

      <div className="mx-auto max-w-full overflow-hidden rounded-md shadow-[0_10px_35px_rgba(0,0,0,0.32)]" style={{ width: clippedWidth }}>
        <style dangerouslySetInnerHTML={{ __html: MINI_STYLE }} />
        <div
          style={{
            width: WIDTH,
            transformOrigin: "top left",
            transform: `scale(${SCALE})`,
            marginBottom: -excess,
            fontFamily: "Tahoma, Arial, sans-serif",
            backgroundColor: "white",
            color: "black",
            lineHeight: 1.6,
            userSelect: "none",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "rgb(0,109,170)", height: 45, padding: "0 16px", color: "white" }}>
            <span style={{ fontSize: 16 }}>Test of Mathematics for University Admission</span>
            <div style={{ textAlign: "right", fontSize: 13, lineHeight: 1.4 }}>
              <div>Time Remaining {question.time}</div>
              <div>{question.num}</div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", backgroundColor: "rgb(70,120,190)", height: 30, padding: "0 8px", color: "white" }}>
            <button className={`pvm-btn-under pvm-ic ${flagged[questionIndex] ? "pvm-ic-flag-sel" : "pvm-ic-flag"}`} onClick={() => setFlagged(current => ({ ...current, [questionIndex]: !current[questionIndex] }))}>
              {flagged[questionIndex] ? "Flagged for Review" : "Flag for Review"}
            </button>
            <span style={{ fontSize: 13, marginLeft: 16 }}>Color Scheme ▾</span>
          </div>

          <div
            style={{
              height: HEIGHT - 45 - 30 - 33,
              padding: transitioning ? 0 : "32px 32px 28px",
              backgroundColor: transitioning ? "rgb(200,200,200)" : "white",
              display: "flex",
              flexDirection: "column",
              alignItems: transitioning ? "center" : "stretch",
              justifyContent: transitioning ? "center" : "flex-start",
            }}
          >
            {transitioning ? (
              <PetalSpinner />
            ) : (
              <>
                <p style={{ fontSize: 13, marginBottom: 28, lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: question.html }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  {question.options.map((option, index) => (
                    <label key={option} onClick={() => setAnswers(current => ({ ...current, [questionIndex]: index }))} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontSize: 13 }}>
                      <input type="radio" name={`pvm-question-${questionIndex}`} readOnly checked={selected === index} style={{ width: 16, height: 16, accentColor: "rgb(0,109,170)", flexShrink: 0, cursor: "pointer" }} />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: "white", height: 33, backgroundColor: "rgb(0,109,170)", borderTop: "2px solid white" }}>
            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
              <div className="pvm-nav-left"><button className="pvm-btn pvm-ic pvm-ic-end"><span><span style={{ textDecoration: "underline" }}>E</span>nd Exam</span></button></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
              <div className="pvm-nav-right"><button className="pvm-btn pvm-ic pvm-ic-prev" onClick={() => navigate(-1)}><span><span style={{ textDecoration: "underline" }}>P</span>revious</span></button></div>
              <div className="pvm-nav-right"><button className="pvm-btn pvm-ic pvm-ic-nav" onClick={() => setShowNavigator(true)}><span>Na<span style={{ textDecoration: "underline" }}>v</span>igator</span></button></div>
              <div className="pvm-nav-right"><button className="pvm-btn pvm-ic pvm-ic-next" onClick={() => navigate(1)}><span><span style={{ textDecoration: "underline" }}>N</span>ext</span></button></div>
            </div>
          </div>

          {showNavigator && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.45)", zIndex: 100 }} onClick={() => setShowNavigator(false)}>
              <div onClick={event => event.stopPropagation()} style={{ width: 420, fontFamily: "Tahoma, Arial, sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.4)", backgroundColor: "rgb(0,109,170)" }}>
                <div style={{ color: "white", padding: "6px 10px", fontSize: "10pt" }}>Navigator - select a question to go to it</div>
                <div style={{ backgroundColor: "white", overflowY: "auto" }}>
                  <table cellSpacing="0" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ backgroundColor: "rgb(70,120,190)", color: "white", fontSize: "10pt" }}>
                      <tr>
                        <th style={{ padding: "5px 10px", textAlign: "left", borderRight: "1px solid #C0C0C0" }}>Question #</th>
                        <th style={{ padding: "5px 10px", textAlign: "left", borderRight: "1px solid #C0C0C0" }}>Status</th>
                        <th style={{ padding: "5px 10px", textAlign: "left" }}>Flagged for Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {QUESTIONS.map((_, index) => {
                        const status = getStatus(index);
                        const current = index === questionIndex;
                        const rowBackground = index % 2 === 0 ? "#F4F4F4" : "#FFFFFF";
                        return (
                          <tr key={index} onClick={() => { goTo(index); setShowNavigator(false); }} style={{ backgroundColor: current ? "#dbeafe" : rowBackground, cursor: "pointer", borderBottom: "1px solid #C0C0C0" }}>
                            <td style={{ padding: "4px 10px", fontSize: "10pt", borderRight: "1px solid #C0C0C0" }}>Question {index + 1}</td>
                            <td style={{ padding: "4px 10px", fontSize: "10pt", color: status === "Complete" ? "black" : "#D90000", borderRight: "1px solid #C0C0C0" }}>{status}</td>
                            <td style={{ padding: "4px 10px", fontSize: "10pt" }}>{flagged[index] ? "Yes" : " "}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ color: "white", padding: "5px 10px", fontSize: "10pt", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{QUESTIONS.filter((_, index) => getStatus(index) !== "Complete").length} Unseen/Incomplete</span>
                  <button onClick={() => setShowNavigator(false)} style={{ border: "1px solid white", backgroundColor: "rgb(0,109,170)", color: "white", padding: "2px 12px", cursor: "pointer" }}><span style={{ textDecoration: "underline" }}>C</span>lose</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
